import api from 'api';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────

// Normalize department → "Human Resources" → "human-resources"
const formatDept = (name) => name?.toLowerCase().replace(/\s+/g, '-');

// 🔥 HOME ROUTE LOGIC (ROLE + PERMISSIONS)
const deriveHome = (user) => {
    if (!user) return '/welcome';

    const perms = user?.permissions || [];
    const roles = user?.roles?.map((r) => r.name) || [];
    const dept = formatDept(user?.department?.name);

    // 1. ADMIN
    if (roles.includes('super_admin') || roles.includes('system_admin')) {
        return '/dashboard/default';
    }
    // 2. DEPARTMENT HEAD
    if (roles.includes('department_head') && dept) {
        if (dept === 'quantity-surveyor') {
            return '/qs/dashboard';
        }
        if (dept === 'civils-engineer') {
            return '/civils/dashboard';
        }
        return `/${dept}/dashboard`;
    }

    if (roles.length > 0 && dept) {
        // Optional mapping for clean routes
        const deptRoutes = {
            'quantity-surveyor': '/qs/dashboard',
            'civils-engineer': '/civils/dashboard',
            accounting: '/accounting/dashboard',
            procurement: '/procurement/dashboard',
            architecture: '/architecture/dashboard'
        };
        return deptRoutes[dept] || `/projects`;
    }

    // 3. GUEST
    if (roles.includes('guest')) {
        return '/welcome';
    }

    // 4. PERMISSION FALLBACK
    if (perms.includes('roads.read')) return '/roads/dashboard';
    if (perms.includes('projects.read')) return '/projects';
    if (perms.includes('reports.read')) return '/reports';

    return '/welcome';
};

// 🔥 ACCESS CONTROL
const deriveAllowed = (user) => {
    const perms = user?.permissions || [];
    const roles = user?.roles?.map((r) => r.name) || [];
    const dept = formatDept(user?.department?.name);

    // ADMIN → FULL ACCESS
    if (roles.includes('super_admin') || roles.includes('system_admin')) {
        return new Set(['*']);
    }

    const allowed = new Set(['/profile', '/welcome', '/403']);

    // DEPARTMENT HEAD
    if (roles.includes('department_head') && dept) {
        allowed.add(`/${dept}`);
        allowed.add(`/${dept}/dashboard`);
    }

    // PERMISSIONS → MODULE ACCESS
    const map = {
        projects: ['/projects'],
        reports: ['/reports'],
        roads: ['/roads'],
        accounting: ['/accounting'],
        procurement: ['/procurement']
    };

    perms.forEach((p) => {
        const mod = p.split('.')[0];
        (map[mod] || []).forEach((r) => allowed.add(r));
    });

    return allowed;
};

// ─────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // ── TOKEN HANDLER
    const setToken = (token) => {
        if (token) {
            sessionStorage.setItem('ACCESS_TOKEN', token);
            api.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
            sessionStorage.removeItem('ACCESS_TOKEN');
            delete api.defaults.headers.common.Authorization;
        }
    };

    // ── FETCH USER
    const fetchMe = useCallback(async () => {
        const token = sessionStorage.getItem('ACCESS_TOKEN');
        if (!token) return setLoading(false);

        setToken(token);

        try {
            const { data } = await api.get('/user');
            setUser(data);
        } catch {
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
        return null;
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    // ── LOGIN
    const login = async (email, password) => {
        const { data } = await api.post('/login', { email, password });

        setToken(data.token);
        setUser(data.user);

        if (data?.user) {
            const home = deriveHome(data.user);
            navigate(home, { replace: true });
        }
    };

    // ── LOGOUT
    const logout = async () => {
        await api.post('/logout');
        setToken(null);
        setUser(null);
        navigate('/pages/login/login3');
    };

    // ── PERMISSIONS
    const perms = user?.permissions || [];
    const roles = user?.roles?.map((r) => r.name) || [];

    const can = useCallback((p) => perms.includes(p), [perms]);
    const canAny = useCallback((arr) => arr.some(can), [can]);
    const canAll = useCallback((arr) => arr.every(can), [can]);

    const isAdmin = roles.includes('super_admin') || roles.includes('system_admin');
    const isHead = roles.includes('department_head');

    // ── ROUTE ACCESS
    const allowed = useMemo(() => deriveAllowed(user), [user?.id, user?.roles, user?.permissions, user?.department?.name]);

    const canVisit = (path) => {
        if (allowed.has('*')) return true;
        return [...allowed].some((p) => path.startsWith(p));
    };

    // ── CONTEXT VALUE
    const value = useMemo(
        () => ({
            user,
            loading,
            token: sessionStorage.getItem('ACCESS_TOKEN'),
            homeRoute: deriveHome(user),
            allowedPrefixes: allowed,

            // actions
            login,
            logout,
            fetchMe,

            // helpers
            can,
            canAny,
            canAll,
            isAdmin,
            isHead,
            canVisit
        }),
        [user, loading, allowed, login, logout, fetchMe, can, canAny, canAll, isAdmin, isHead, canVisit]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────
export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
};
